export function transformResourceComponentsToWiki(tiptapHtmlString: string): string {
    // Parse the HTML string into a document
    const parser = new DOMParser();
    const doc = parser.parseFromString(tiptapHtmlString, 'text/html');
  
    // Select all <resource-component> elements
    const resources = doc.querySelectorAll('resource-component');
    resources.forEach(resource => {
      const resourceUUID = resource.getAttribute('resourceuuid') || resource.getAttribute('resourceUUID') || '';
      const label = resource.textContent?.trim() || '';
  
      let replacementText = '';
      if (resourceUUID && label) {
        replacementText = `[[ ${resourceUUID} | ${label} ]]`;
      }
  
      // Replace the resource-component element with a text node containing the replacement text
      const textNode = doc.createTextNode(replacementText);
      resource.parentNode?.replaceChild(textNode, resource);
    });
  
    // Return the updated HTML content
    return doc.body.innerHTML;
  }
  
  export function transformWikiToResourceComponents(wikiText: string): string {
    const regex = /\[\[ *([^|<>\]]+?) *\| *([^\][]+?) *\]\]/gi
    const withResourceComponents = wikiText.replace(regex, (match, resourceUUID, label) => {
      resourceUUID = resourceUUID.trim();
      label = label ? label.trim() : resourceUUID;
      return `<resource-component resourceuuid="${resourceUUID}">${label}</resource-component>`;
    });
    return normalizeFakeBulletLists(withResourceComponents);
  }

export function countWikitextParts(wikiText: string): number {
  const regex = /\[\[ *([^|<>\]]+?) *\| *([^\][]+?) *\]\]/gi;
  const matches = wikiText.match(regex);
  return matches ? matches.length : 0;
}

// Present Levels' AI generation is instructed (via the Airtable-hosted system prompt; see
// services/presentlevels/systemprompts/mock.go for the local mirror) to format assessment/
// citation lists as "<br>&nbsp;&nbsp;&nbsp;&nbsp;• item" inside a single <p> — a visual bullet
// list with no <ul>/<li> semantics (WCAG 1.3.1 semantic-list). tiptap's StarterKit already
// renders real lists correctly, so this rewrites that shape into one before it reaches the
// editor, for both freshly generated and already-stored statement text.
const BULLET_PREFIX_REGEX = /^[ \t ]*•[ \t ]*/

function splitParagraphIntoLines(p: Element): ChildNode[][] {
  const lines: ChildNode[][] = [[]]
  Array.from(p.childNodes).forEach(node => {
    if (node.nodeName === 'BR') {
      lines.push([])
    } else {
      lines[lines.length - 1].push(node)
    }
  })
  return lines
}

function isBlankLine(line: ChildNode[]): boolean {
  return line.every(node => !(node.textContent ?? '').trim())
}

function lineStartsWithBullet(line: ChildNode[]): boolean {
  const [first] = line
  return !!first && first.nodeType === Node.TEXT_NODE && BULLET_PREFIX_REGEX.test(first.textContent ?? '')
}

function stripBulletPrefix(line: ChildNode[]): ChildNode[] {
  const [first, ...rest] = line
  const stripped = (first.textContent ?? '').replace(BULLET_PREFIX_REGEX, '')
  if (!stripped) {
    return rest
  }
  first.textContent = stripped
  return [first, ...rest]
}

export function normalizeFakeBulletLists(html: string): string {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  doc.querySelectorAll('p').forEach(p => {
    const lines = splitParagraphIntoLines(p).filter(line => !isBlankLine(line))
    if (lines.length === 0 || !lines.every(lineStartsWithBullet)) {
      return
    }

    const list = doc.createElement('ul')
    Array.from(p.attributes).forEach(attr => list.setAttribute(attr.name, attr.value))
    lines.forEach(line => {
      const li = doc.createElement('li')
      stripBulletPrefix(line).forEach(node => li.appendChild(node))
      list.appendChild(li)
    })
    p.replaceWith(list)
  })

  return doc.body.innerHTML
}
